import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, ButtonGroup, Card, CardBody, CardHeader, Flex, FormControl, FormLabel, Heading, IconButton, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, useDisclosure, VStack } from '@chakra-ui/react'
import { FILE_ATTACHMENT_STATUS, FileAttachment, UiBreadcrumb, UiSelect } from 'components/common'
import { createArtworkRequestNote, deleteArtworkRequestNoteAttachmentByPath, getAdhocQuoteByNumber, getArtworkRequestById, getArtworkRequestNotesByRequestId, getEdgeQuoteByNumber, getUsersByRoles, updateArtworkRequest, uploadArtworkRequestNoteAttachment } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { useParams } from 'react-router-dom'
import { ARTWORK_REQUEST_STATUSES, getAssetUrl, ROLES } from 'lib'
import { format as formatDate } from 'date-fns'
import { useImmer } from 'use-immer'
import { MdAttachment } from 'react-icons/md'

const BreadcrumbNav = [
  {
    label: 'Artwork Requests',
    path: '/artwork-requests/queue'
  },
  {
    label: 'View',
    isCurrentPage: true
  }
]

export const ViewArtworkRequest = () => {
  const { requestId } = useParams()
  const { createToast, currentUser } = useAppContext()
  const [request, setRequest] = useState(null)
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useImmer({
    notes: '',
    attachments: []
  })
  const fileInputRef = useRef(null)
  const [nextAttachmentId, setNextAttachmentId] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStatusChanging, setIsStatusChanging] = useImmer({
    [ARTWORK_REQUEST_STATUSES.ReadyForReview]: false,
    [ARTWORK_REQUEST_STATUSES.PendingRevision]: false,
    [ARTWORK_REQUEST_STATUSES.Completed]: false
  })
  const disclosureReassignGraphicDesigner = useDisclosure()

  const load = async () => {
    try {
      const response = await getArtworkRequestById(requestId)
      if (!response.openedAt && currentUser?.role === ROLES.GraphicDesigner) {
        const now = new Date()
        const formattedNow = formatDate(new Date(now.getTime() + now.getTimezoneOffset() * 60000), 'yyyy-MM-dd HH:mm:ss')
        await updateArtworkRequest(response.id, { openedAt: formattedNow })
        response.openedAt = formattedNow
      }
      setRequest(response)
    } catch (e) {
      createToast('Failed to load artwork request')
    }

    try {
      const response = await getArtworkRequestNotesByRequestId(requestId)
      setNotes(response)
    } catch (e) {
      createToast('Failed to load artwork request notes')
    }
  }

  useEffect(() => {
    load()
  }, [currentUser?.role])

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files
      // eslint-disable-next-line space-unary-ops
      for (let i = 0; i < files.length; i ++) {
        const file = files[i]
        const curAttachmentId = nextAttachmentId + i
        const attachment = {
          id: curAttachmentId,
          status: 'uploading',
          progress: 0
        }
        setNewNote(draft => { draft.attachments = [...draft.attachments, attachment] })
        try {
          const response = await uploadArtworkRequestNoteAttachment({ file }, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setNewNote(draft => {
              draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, progress: percentCompleted } : v)
            })
          })
          setNewNote(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...response, status: 'success' } : v)
          })
        } catch (err) {
          setNewNote(draft => {
            draft.attachments = draft.attachments.map(v => v.id === curAttachmentId ? { ...v, status: 'fail' } : v)
          })
        }
      }
      setNextAttachmentId(nextAttachmentId + files.length)
    }
  }

  const onAddAttachment = () => {
    fileInputRef.current.click()
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    try {
      const response = await createArtworkRequestNote({ requestId: request?.id, notes: newNote.notes, attachments: newNote.attachments })
      setNotes([response, ...notes])
      setIsSubmitting(false)
      createToast('success', 'Success', 'You added a note')
      setNewNote({ notes: '', attachments: [] })
    } catch (e) {
      createToast('error', 'Error', 'Failed to add a note')
    }
  }

  const onUpdateRequestStatus = async (status) => {
    setIsStatusChanging(draft => { draft[status] = true })
    try {
      const response = await updateArtworkRequest(request?.id, { status })
      setRequest(response)
      setIsStatusChanging(draft => { draft[status] = false })
      createToast('success', 'Success', 'Request status updated')
    } catch (e) {
      createToast('error', 'Error', 'Failed to update request status')
      setIsStatusChanging(draft => { draft[status] = false })
    }
  }

  const onOpenQuotePDF = async (quoteNumber) => {
    try {
      const quote = await getEdgeQuoteByNumber(quoteNumber)
      if (quote && quote.pdf) {
        window.open(getAssetUrl(quote.pdf), '_blank')
        return
      }
    } catch (e) {
    }

    try {
      const quote = await getAdhocQuoteByNumber(quoteNumber)
      if (quote && quote.pdf) {
        window.open(getAssetUrl(quote.pdf), '_blank')
        return
      }
    } catch (e) {
    }

    createToast('info', 'Info', 'Quote number is not correct')
  }

  const actionButtons = useMemo(() => {
    if (!currentUser || !request) {
      return []
    }
    if (currentUser.role === ROLES.GraphicDesigner || currentUser.role === ROLES.SeniorGraphicDesigner) {
      if (request.status !== ARTWORK_REQUEST_STATUSES.Completed && request.status !== ARTWORK_REQUEST_STATUSES.ReadyForReview) {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => onUpdateRequestStatus(ARTWORK_REQUEST_STATUSES.ReadyForReview)}
            isLoading={isStatusChanging[ARTWORK_REQUEST_STATUSES.ReadyForReview]}
            mb={4}
          >
            {ARTWORK_REQUEST_STATUSES.ReadyForReview}
          </Button>
        )
      }
    } else {
      if (request.status === ARTWORK_REQUEST_STATUSES.ReadyForReview) {
        return (
          <>
            <Flex columnGap={4} mb={4}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => onUpdateRequestStatus(ARTWORK_REQUEST_STATUSES.Completed)}
                isLoading={isStatusChanging[ARTWORK_REQUEST_STATUSES.Completed]}
              >
                Approve As-Is
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => onUpdateRequestStatus(ARTWORK_REQUEST_STATUSES.PendingRevision)}
                isLoading={isStatusChanging[ARTWORK_REQUEST_STATUSES.PendingRevision]}
              >
                Request Revision
              </Button>
            </Flex>
          </>
        )
      }
    }
  }, [currentUser, request, isStatusChanging[ARTWORK_REQUEST_STATUSES.ReadyForReview], isStatusChanging[ARTWORK_REQUEST_STATUSES.Completed], isStatusChanging[ARTWORK_REQUEST_STATUSES.PendingRevision]])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">View Artwork Request</Heading>
        </Flex>
        <Card borderRadius={0}>
          <CardHeader bg="gray.500" p={4}>
            <Heading color="white" size="sm">Information</Heading>
          </CardHeader>
          <CardBody p={8}>
            <Flex justifyContent="space-between">
              <Box>
                {(request && request.quoteNumber > 0) && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => onOpenQuotePDF(request.quoteNumber)}
                    mb={4}
                  >
                    View Quote PDF (#{request.quoteNumber})
                  </Button>
                )}
                <Text fontWeight="bold" mb={4}>Designer: {request?.assignedGraphicDesignerName}</Text>
                <Text fontWeight="bold" mb={4}>Status: {request?.status}</Text>
              </Box>
              {(request && currentUser && currentUser.role !== ROLES.GraphicDesigner) && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={disclosureReassignGraphicDesigner.onOpen}
                >
                  Reassign Graphic Designer
                </Button>
              )}
            </Flex>
            {actionButtons}
            {notes.map(note => {
              return (
                <VStack key={note.id} gap={2} mt={4} alignItems="flex-start" fontSize="xs" position="relative">
                  <Flex gap={4} alignItems="center">
                    <Image w="28px" h="28px" src={getAssetUrl(note.authorImage)} borderRadius="full" />
                    <Text fontWeight="bold">{note.authorName}</Text>
                    <Text ml={4}>{formatDate(new Date(note.createdAt), 'MM/dd/yyyy hh:mm:ss bbbb')}</Text>
                  </Flex>
                  <Box ml={8}>
                    <Text whiteSpace="pre-wrap">{note.notes}</Text>
                    {note.attachments.length !== 0 && (
                      <Flex gap={4} flexWrap="wrap" my={4}>
                        {note.attachments.map(attachment => {
                          return (
                            <FileAttachment
                              key={attachment.path}
                              canRemove={false}
                              id={attachment.id}
                              status={FILE_ATTACHMENT_STATUS.Original}
                              progress={0}
                              filename={attachment.filename || ''}
                              extension={attachment.extension || ''}
                              path={attachment.path || ''}
                              canDownload={true}
                            />
                          )
                        })}
                      </Flex>
                    )}
                  </Box>
                </VStack>
              )
            })}
            <Box my={8}>
              <Textarea
                rows="4"
                value={newNote.notes}
                onChange={(e) => setNewNote(draft => { draft.notes = e.target.value })}
              />
              {newNote.attachments.length !== 0 && (
                <Flex gap={4} flexWrap="wrap" my={4}>
                  {newNote.attachments.map(attachment => {
                    return (
                      <FileAttachment
                        key={attachment.status === FILE_ATTACHMENT_STATUS.Success ? attachment.path : attachment.id}
                        canRemove={true}
                        id={attachment.id}
                        status={attachment.status}
                        progress={attachment.progress}
                        filename={attachment.filename || ''}
                        extension={attachment.extension || ''}
                        path={attachment.path || ''}
                        onRemove={async () => {
                          if (attachment.status === FILE_ATTACHMENT_STATUS.Success) {
                            try {
                              await deleteArtworkRequestNoteAttachmentByPath({ path: attachment.path })
                            } catch (err) {
                              console.log('Failed to remove temp file on the server.')
                            }
                          }
                          setNewNote(draft => {
                            draft.attachments = draft.attachments.filter(v => {
                              return attachment.status === FILE_ATTACHMENT_STATUS.Success ? (v.path !== attachment.path) : (v.id !== attachment.id)
                            })
                          })
                        }}
                      />
                    )
                  })}
                </Flex>
              )}
              <Flex justifyContent="space-between" my={2}>
                <Flex gap={2} cursor="pointer" alignItems="center" onClick={onAddAttachment}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    colorScheme="gray"
                    icon={<MdAttachment fontSize="18px" />}
                  />
                  <Text fontSize="xs">Add Attachment</Text>
                  <Input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} multiple />
                </Flex>
                <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={isSubmitting}>Add</Button>
              </Flex>
            </Box>
          </CardBody>
        </Card>
      </Box>
      {request && disclosureReassignGraphicDesigner.isOpen && (
        <ModalReassignGraphicDesigner
          request={request}
          reload={load}
          {...disclosureReassignGraphicDesigner}
        />
      )}
    </>
  )
}

export const ModalReassignGraphicDesigner = ({ isOpen, onClose, request, reload }) => {
  const { createToast } = useAppContext()
  const [designer, setDesigner] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!designer) {
      createToast('error', 'Error', 'Please select a designer.')
      return
    }

    setIsSaving(true)
    try {
      await updateArtworkRequest(request.id, { assignedGraphicDesignerId: designer.id })
      setIsSaving(false)
      createToast('success', 'Success', 'Reassigned the designer.')
      onClose()
      reload()
    } catch (err) {
      createToast('error', 'Error', 'Failed to reassign.')
      setIsSaving(false)
    }
  }

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reassign Graphic Designer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Assigned Graphic Designer</FormLabel>
              <UiSelect
                value={designer}
                onChange={v => setDesigner(v)}
                loadData={(page, search) => getUsersByRoles(page, 20, search, 'name', 'asc', [ROLES.GraphicDesigner, ROLES.SeniorGraphicDesigner], true)}
                apiRespProp="rows"
                getOptionValue={v => v.id}
                getOptionLabel={v => v.name}
                placeholder="Search Users"
                noOptionsMessage={() => 'No Users Found'}
                required={true}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup gap={4}>
              <Button colorScheme="blue" size="sm" onClick={handleSave} isLoading={isSaving}>Save</Button>
              <Button variant="ghost" colorScheme="gray" size="sm" onClick={onClose}>Cancel</Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

ModalReassignGraphicDesigner.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  request: PropTypes.object,
  reload: PropTypes.func
}
